import React, {FC} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import classNames from 'classnames';
import AnimateHeight from 'react-animate-height';
import {connect} from 'react-redux';

import {CHECKOUT_GENERAL} from 'constants/analytics/events/checkout';
import {PRODUCT_TYPES} from 'constants/product';
import {ApplicationStoreType} from 'desktop/store';
import {CheckoutItemType} from 'desktop/types/checkoutItemsType';
import {getFeatureAwareImage} from 'desktop/utils/lensTintsEg/lensTintsEg';
import {getCloudinaryURL} from 'libs/cloudinary/cloudinary';

import {getOrderReviewItemById} from '../../store/selectors/orderReviewItems/orderReviewItems';
import {CartItemDetailsConnected} from '../cartItemDetails/cartItemDetails';
import {CartItemPreview} from '../cartItemPreview/cartItemPreview';
import {GlassesCartItemDetails} from '../glassesCartItemDetails/glassesCartItemDetails';
import {LensesCartItemDetails} from '../lensesCartItemDetails/lensesCartItemDetails';

import s from './cartItem.scss';

export type PropsType = {
    item?: CheckoutItemType;
    itemId: number;
    isOpen: boolean;
    onDetailsClick: (id: number) => void;
};

/**
 * Get image size by product type
 * @param {string} type - product type
 * @return {string} - url params
 */
const getItemImageSize = (type: string): string => {
    switch (type) {
        case PRODUCT_TYPES.CONTACT_LENSES:
            return s.contactLenses;
        case PRODUCT_TYPES.ACCESSORY:
            return s.accessory;
        default:
            return s.glasses;
    }
};

/**
 * Cart Item for checkout page
 *
 * @constructor
 */
export const CartItem: FC<PropsType> = ({isOpen, item, onDetailsClick}) => {
    if (!item) return null;

    const renderCartItem = () => {
        switch (item.product.type) {
            case PRODUCT_TYPES.EYEGLASSES:
            case PRODUCT_TYPES.SUNGLASSES:
                return <GlassesCartItemDetails item={item} />;
            case PRODUCT_TYPES.CONTACT_LENSES:
                return <LensesCartItemDetails item={item} />;
            default:
                return <CartItemDetailsConnected item={item} />;
        }
    };

    const togglePrescriptionDetails = () => {
        sendDesktopAnalyticsEvent(CHECKOUT_GENERAL, {
            message: `${isOpen ? 'Collapse' : 'Expand'} Details`,
        });

        onDetailsClick(item.id);
    };
    const isShowDetails =
        item.product.type !== 'accessories' &&
        item.options &&
        Boolean(Object.keys(item.options).length);

    const isAccessories = item.product.type === 'accessories';
    return (
        <section className={classNames(s.wrapper, {[s.wrapperForAccessories]: isAccessories})}>
            <header className={s.header}>
                <div
                    style={{
                        backgroundImage: `url(${getCloudinaryURL(getFeatureAwareImage(item.product, item.product.image, item.options))})`,
                    }}
                    aria-label={item.windowName}
                    className={classNames(s.image, getItemImageSize(item.product.type))}
                    role="presentation"
                />
                <CartItemPreview
                    isOpen={isOpen}
                    isShowDetails={isShowDetails}
                    item={item}
                    togglePrescriptionDetails={togglePrescriptionDetails}
                />
            </header>
            {isShowDetails && (
                <AnimateHeight duration={200} easing="linear" height={isOpen ? 'auto' : 0}>
                    {renderCartItem()}
                </AnimateHeight>
            )}
        </section>
    );
};

const mapStateToProps = (store: ApplicationStoreType, props: {itemId: number}) => ({
    item: getOrderReviewItemById(props.itemId)(store),
});

export const CartItemConnected = connect(mapStateToProps)(CartItem);
